import React, { useEffect, useState } from "react";
import { PhoneCall, Loader2, X } from "lucide-react";
import { api } from "../../lib/api";
import { useBranding } from "../../lib/branding";

const DISPOSITIONS = [
  "Booked", "Hot Lead", "Got Email", "Not Interested", "Asked for DNC",
  "Hung Up", "Wrong Business", "Voicemail", "No Answer", "Disconnected", "Ghost Call",
];

const DISP_TINT = {
  Booked: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  "Hot Lead": "bg-amber-500/10 text-amber-300 border-amber-500/30",
  "Got Email": "bg-sky-500/10 text-sky-300 border-sky-500/30",
};

export default function PortalCalls() {
  const branding = useBranding();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);

  const accent = branding?.accent_color || "#FFB000";

  useEffect(() => {
    setLoading(true);
    const url = filter ? `/portal/calls?disposition=${encodeURIComponent(filter)}` : "/portal/calls";
    api.get(url).then(({ data }) => {
      setItems(data.items || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [filter]);

  return (
    <div className="space-y-6" data-testid="portal-calls-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-medium mb-2" style={{ color: accent }}>Call Log</div>
        <h1 className="text-3xl font-bold tracking-tight">Every call, every time</h1>
        <p className="text-sm text-white/50 mt-2">Tap any call for the transcript, recording, and AI summary.</p>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2" data-testid="portal-calls-filters">
        <Chip active={!filter} onClick={() => setFilter("")} accent={accent}>All</Chip>
        {DISPOSITIONS.map((d) => (
          <Chip key={d} active={filter === d} onClick={() => setFilter(filter === d ? "" : d)} accent={accent}>
            {d}
          </Chip>
        ))}
      </div>

      {loading ? (
        <div className="text-white/50 text-sm flex items-center gap-2 py-10"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-[#121214] border border-white/5 rounded-2xl p-12 text-center" data-testid="portal-calls-empty">
          <PhoneCall className="w-10 h-10 text-white/20 mx-auto mb-4" strokeWidth={1.5} />
          <div className="text-white/70 font-medium">No calls yet</div>
          <div className="text-white/40 text-sm mt-1">When your AI starts taking calls they'll show up here.</div>
        </div>
      ) : (
        <div className="bg-[#121214] border border-white/5 rounded-2xl divide-y divide-white/5 overflow-hidden">
          {items.map((c) => (
            <button
              key={c.id}
              onClick={() => setOpen(c)}
              data-testid={`portal-call-row-${c.id}`}
              className="w-full text-left px-5 py-4 hover:bg-white/[0.02] transition-colors flex items-center gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-0.5">
                  <span className="text-sm font-medium text-white/90 truncate">{c.contact_name || c.phone || "Unknown"}</span>
                  {c.disposition && (
                    <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded border font-medium ${DISP_TINT[c.disposition] || "bg-white/5 text-white/60 border-white/10"}`}>
                      {c.disposition}
                    </span>
                  )}
                </div>
                <div className="text-[12px] text-white/40 flex items-center gap-3 flex-wrap">
                  <span>{c.phone}</span>
                  {c.duration_seconds != null && <><span>·</span><span>{Math.round(c.duration_seconds)}s</span></>}
                  {c.created_at && <><span>·</span><span>{new Date(c.created_at).toLocaleString()}</span></>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Slide-out detail */}
      {open && <CallDetailDrawer call={open} onClose={() => setOpen(null)} accent={accent} />}
    </div>
  );
}

function Chip({ children, active, onClick, accent }) {
  return (
    <button
      onClick={onClick}
      className={`text-[11px] uppercase tracking-[0.1em] font-medium px-3 py-1.5 rounded-full border transition-colors ${active ? "" : "text-white/55 border-white/10 hover:text-white"}`}
      style={active ? { background: `${accent}18`, color: accent, borderColor: `${accent}40` } : {}}
    >
      {children}
    </button>
  );
}

function CallDetailDrawer({ call, onClose, accent }) {
  return (
    <div className="fixed inset-0 z-40 flex" data-testid="portal-call-drawer">
      <div className="flex-1 bg-black/50" onClick={onClose} />
      <div className="w-full max-w-xl bg-[#0F0F11] border-l border-white/10 overflow-y-auto">
        <div className="px-6 py-5 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#0F0F11]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] font-medium" style={{ color: accent }}>
              {call.disposition || "No disposition"}
            </div>
            <div className="text-base font-semibold mt-0.5">{call.contact_name || call.phone}</div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="portal-call-drawer-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Detail label="Phone">{call.phone}</Detail>
            <Detail label="Duration">{call.duration_seconds ? `${Math.round(call.duration_seconds)}s` : "—"}</Detail>
            <Detail label="Status">{call.status || "—"}</Detail>
            <Detail label="When">{call.created_at ? new Date(call.created_at).toLocaleString() : "—"}</Detail>
          </div>

          {call.recording_url && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/40 mb-2">Recording</div>
              <audio controls src={call.recording_url} className="w-full" data-testid="portal-call-recording" />
            </div>
          )}

          {call.summary && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/40 mb-2">AI Summary</div>
              <div className="bg-[#121214] border border-white/5 rounded-lg p-4 text-sm text-white/85 leading-relaxed" data-testid="portal-call-summary">
                {call.summary}
              </div>
            </div>
          )}

          {call.transcript && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/40 mb-2">Transcript</div>
              <pre className="bg-[#121214] border border-white/5 rounded-lg p-4 text-[12.5px] text-white/75 leading-relaxed whitespace-pre-wrap font-mono max-h-96 overflow-auto" data-testid="portal-call-transcript">
                {call.transcript}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.12em] text-white/40 mb-1">{label}</div>
      <div className="text-sm text-white/85">{children}</div>
    </div>
  );
}
